import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Globe, Plus, CalendarClock, Wallet, Send, Crosshair, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PIPELINE, STAGE_LABEL, STAGE_INDEX, stageFarge, fmtSiden, datoIdag, tierFarge } from "./eventosUtils";
import { ReadinessBadge, ReadinessListe } from "./Readiness";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const PersonFelt = ({ label, verdi, onLagre, testid, textarea }) => {
  const [v, setV] = useState(verdi || "");
  useEffect(() => { setV(verdi || ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (verdi || "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function SpeakerDrawer({ speaker, members, aktor, onPatch, onReload, onDelete, onLukk }) {
  const [saker, setSaker] = useState(null);
  const [nySak, setNySak] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  useEffect(() => {
    let aktiv = true;
    api.get("/admin/tasks").then(({ data }) => {
      if (!aktiv) return;
      setSaker((data.tasks || []).filter((t) => (t.linkedEntities || []).some((le) => le.type === "speaker" && le.id === speaker.id)));
    }).catch(() => setSaker([]));
    return () => { aktiv = false; };
  }, [speaker.id]);

  const person = speaker.person || {};
  const patchPerson = (felt) => (v) => onPatch({ person: { [felt]: v } });

  const lagNySak = async () => {
    const t = nySak.trim();
    if (!t) return;
    try {
      const { data } = await api.post("/admin/tasks", {
        title: t,
        actor: aktor,
        linkedEntities: [{ type: "speaker", id: speaker.id, label: person.name }],
      });
      setSaker((prev) => [data.task, ...(prev || [])]);
      setNySak("");
      toast.success("Sak opprettet og koblet");
    } catch (e) {
      toast.error("Kunne ikke opprette sak");
    }
  };

  const aktivitet = [...(speaker.activity || [])].reverse();

  const lagBudsjettlinje = async () => {
    try {
      await api.post(`/admin/os/speakers/${speaker.id}/budget-line`, { actor: aktor });
      toast.success("Budsjettlinje opprettet — fee synkes automatisk");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette budsjettlinje");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="speaker-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[600px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        {/* Topp */}
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[19px] font-semibold leading-tight" data-testid="speaker-drawer-navn">{person.name}</h2>
              <p className="text-[12px] text-neutral-400 mt-0.5">
                {[person.title, person.org].filter(Boolean).join(" · ") || "—"}
              </p>
              {speaker.fromTargetTier && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold rounded-full border border-[#E7E4DB] px-2 py-0.5 text-neutral-500" data-testid="speaker-fra-target">
                  <Crosshair size={9} style={{ color: tierFarge(speaker.fromTargetTier) }} /> Fra target-listen · Tier {speaker.fromTargetTier}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => onPatch({ published: !speaker.published })}
                className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
                  speaker.published
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "text-neutral-500 border-[#DED9CD] hover:text-neutral-800"
                }`}
                data-testid="speaker-publiser-btn"
                title="Styrer om speakeren vises på nettsiden"
              >
                <Globe size={12} /> {speaker.published ? "Publisert" : "Ikke publisert"}
              </button>
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="speaker-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="speaker-drawer-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <select
              value={speaker.stage}
              onChange={(e) => onPatch({ stage: e.target.value })}
              className="text-[12.5px] font-medium border rounded-lg px-2.5 py-1.5 bg-white focus:outline-none transition-colors"
              style={{ borderColor: stageFarge(speaker.stage), color: stageFarge(speaker.stage) }}
              data-testid="speaker-stage-select"
            >
              {PIPELINE.map((s) => (
                <option key={s.k} value={s.k}>{s.label}</option>
              ))}
            </select>
            <ReadinessBadge computed={speaker.computed} size="md" />
          </div>
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <button
              type="button"
              onClick={() => onPatch({ stage: "verbal", signal: null, ball: null })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={(STAGE_INDEX[speaker.stage] ?? 0) >= 5 && speaker.stage !== "declined"
                ? { background: "#10B981", borderColor: "#10B981", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#10B981" }}
              title="Sett Muntlig ja"
              data-testid="speaker-signal-yes"
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => onPatch({ signal: speaker.signal === "maybe" ? null : "maybe", ball: speaker.signal === "maybe" ? null : "us" })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={speaker.signal === "maybe" || speaker.stage === "interested"
                ? { background: "#D97706", borderColor: "#D97706", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#D97706" }}
              title="Sett/fjern kanskje"
              data-testid="speaker-signal-maybe"
            >
              Kanskje
            </button>
            <button
              type="button"
              onClick={() => onPatch(speaker.stage === "declined"
                ? { stage: "outreach_sent", declineReason: "", signal: null }
                : { stage: "declined", signal: null, ball: null })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={speaker.stage === "declined"
                ? { background: "#DC2626", borderColor: "#DC2626", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#DC2626" }}
              title={speaker.stage === "declined" ? "Gjenåpne — tilbake til Kontaktet" : "Sett Avslått (grunn settes i Outreach-listen)"}
              data-testid="speaker-signal-no"
            >
              Nei
            </button>
            <button
              type="button"
              onClick={() => { onPatch({ lastContact: datoIdag(), ball: "them" }); toast.success("Kontakt logget"); }}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 rounded-full px-2.5 py-1 border border-[#E7E4DB] hover:border-neutral-400 hover:text-neutral-800 transition-colors"
              data-testid="speaker-logg-kontakt"
            >
              <Send size={10.5} /> Logg kontakt
            </button>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400" title="Oppfølgingsdato">
              Følg opp
              <input
                type="date"
                value={speaker.followUp || ""}
                onChange={(e) => onPatch({ followUp: e.target.value || "" })}
                className="text-[11px] border border-[#E7E4DB] rounded-lg px-1.5 py-0.5 bg-white text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
                data-testid="speaker-folgopp"
              />
            </label>
            {speaker.lastContact && (
              <span className="bud-mono text-[10.5px] text-neutral-400 tabular-nums" data-testid="speaker-sist-kontakt">
                sist {fmtSiden(speaker.lastContact)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap" data-testid="speaker-owner-rad">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400 mr-0.5">Ansvarlig</span>
            {(members || []).map((m) => {
              const aktivEier = speaker.ownerId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onPatch({ ownerId: aktivEier ? null : m.id })}
                  title={aktivEier ? `Fjern ${m.name} som ansvarlig` : `Sett ${m.name} som ansvarlig`}
                  className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-full border pl-1 pr-2.5 py-0.5 transition-colors ${
                    aktivEier
                      ? "bg-neutral-900 border-neutral-900 text-white"
                      : "border-[#DED9CD] text-neutral-500 hover:text-neutral-800 hover:border-neutral-400"
                  }`}
                  data-testid={`speaker-owner-pill-${m.id}`}
                >
                  {m.bilde
                    ? <img src={m.bilde} alt="" className="w-[18px] h-[18px] rounded-full object-cover shrink-0" />
                    : (
                      <span className="w-[18px] h-[18px] rounded-full text-[9px] font-bold text-white inline-flex items-center justify-center shrink-0" style={{ background: m.color || "#8B5CF6" }}>
                        {m.name[0]}
                      </span>
                    )}
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Readiness">
            <ReadinessListe computed={speaker.computed} onSett={(r) => onPatch({ readiness: r })} testidPrefix="speaker" />
          </Seksjon>

          <Seksjon label="Offentlig profil (projiseres til nettsiden)">
            <div className="grid grid-cols-2 gap-3">
              <PersonFelt label="Navn" verdi={person.name} onLagre={patchPerson("name")} testid="speaker-person-navn" />
              <PersonFelt label="Rolle/tittel" verdi={person.title} onLagre={patchPerson("title")} testid="speaker-person-tittel" />
              <PersonFelt label="Organisasjon" verdi={person.org} onLagre={patchPerson("org")} testid="speaker-person-org" />
              <PersonFelt label="Bilde-URL" verdi={person.photoUrl} onLagre={patchPerson("photoUrl")} testid="speaker-person-foto" />
              <div className="col-span-2">
                <PersonFelt label="Bio" verdi={person.bio} onLagre={patchPerson("bio")} testid="speaker-person-bio" textarea />
              </div>
              <label className="adm-label">
                Visningsrekkefølge (nettside)
                <input
                  type="number"
                  defaultValue={speaker.publishedOrder ?? 99}
                  onBlur={(e) => Number(e.target.value) !== speaker.publishedOrder && onPatch({ publishedOrder: Number(e.target.value) })}
                  className="adm-input"
                  data-testid="speaker-rekkefolge"
                />
              </label>
            </div>
          </Seksjon>

          <Seksjon label="Internt (vises aldri offentlig)">
            <div className="grid grid-cols-2 gap-3">
              <PersonFelt label="E-post" verdi={person.email} onLagre={patchPerson("email")} testid="speaker-person-epost" />
              <PersonFelt label="Telefon" verdi={person.phone} onLagre={patchPerson("phone")} testid="speaker-person-telefon" />
              <label className="adm-label">
                Fee (NOK)
                <input
                  type="number"
                  defaultValue={speaker.fee ?? 0}
                  onBlur={(e) => Number(e.target.value) !== speaker.fee && onPatch({ fee: Number(e.target.value) })}
                  className="adm-input"
                  data-testid="speaker-fee-input"
                />
              </label>
              <label className="adm-label">
                Intern ansvarlig
                <select
                  value={speaker.ownerId || ""}
                  onChange={(e) => onPatch({ ownerId: e.target.value || null })}
                  className="adm-input"
                  data-testid="speaker-owner-select"
                >
                  <option value="">Ingen</option>
                  {(members || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
              <label className="adm-label">
                Program role
                <select
                  value={speaker.programRole || ""}
                  onChange={(e) => onPatch({ programRole: e.target.value })}
                  className="adm-input"
                  data-testid="speaker-programrole-select"
                >
                  <option value="">—</option>
                  <option value="headline">Headline</option>
                  <option value="anchor">Anchor</option>
                  <option value="credibility">Credibility</option>
                  <option value="supporting">Supporting / Specialist</option>
                </select>
              </label>
              <label className="adm-label">
                Outreach priority
                <select
                  value={speaker.outreachPriority || ""}
                  onChange={(e) => onPatch({ outreachPriority: e.target.value })}
                  className="adm-input"
                  data-testid="speaker-prioritet-select"
                >
                  <option value="">—</option>
                  <option value="now">NOW</option>
                  <option value="next">NEXT</option>
                  <option value="watch">WATCH</option>
                  <option value="moonshot">MOONSHOT</option>
                </select>
              </label>
              <div className="col-span-2">
                <PersonFelt label="Next move" verdi={speaker.nextStep} onLagre={(v) => onPatch({ nextStep: v })} testid="speaker-nextstep-input" />
              </div>
            </div>
            <div className="mt-3">
              <PersonFelt label="Interne notater" verdi={speaker.notes} onLagre={(v) => onPatch({ notes: v })} testid="speaker-notater" textarea />
            </div>
          </Seksjon>

          <Seksjon label="Budsjett">
            {speaker.budgetLineId ? (
              <button
                type="button"
                onClick={() => nav("/admin/budsjett")}
                className="text-[12.5px] text-neutral-600 underline underline-offset-2 hover:text-neutral-900 transition-colors"
                data-testid="speaker-til-budsjett"
              >
                Koblet til budsjettlinje — fee synkes automatisk. Åpne budsjett →
              </button>
            ) : (
              <button type="button" onClick={lagBudsjettlinje} className="adm-btn-ghost adm-btn-sm" data-testid="speaker-lag-budsjettlinje">
                <Wallet size={12} /> Opprett budsjettlinje fra fee ({Number(speaker.fee || 0).toLocaleString("nb-NO")} NOK)
              </button>
            )}
          </Seksjon>

          <Seksjon label={`Sessions (${(speaker.sessions || []).length})`}>
            {(speaker.sessions || []).length === 0 ? (
              <p className="text-[12px] text-neutral-400">Ikke koblet til noen session ennå — gjøres fra Program.</p>
            ) : (
              speaker.sessions.map((s) => (
                <p key={s.id} className="flex items-center gap-2 text-[12.5px] text-neutral-700 py-1">
                  <CalendarClock size={12} className="text-neutral-300" /> {s.title}
                </p>
              ))
            )}
          </Seksjon>

          <Seksjon label={`Saker (${saker == null ? "…" : saker.length})`}>
            {(saker || []).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => nav(`/admin/saker?aapne=${t.id}`)}
                title="Åpne saken i Saker"
                className="group w-full flex items-center gap-2 text-[12.5px] py-1 px-1 -mx-1 rounded-md text-left hover:bg-neutral-50 transition-colors"
                data-testid={`speaker-sak-${t.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className={`truncate ${t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-700 group-hover:text-neutral-900"}`}>{t.title}</span>
                {t.dueDate && <span className="text-[10.5px] text-neutral-400 ml-auto shrink-0 tabular-nums">{t.dueDate}</span>}
                <ArrowUpRight size={12} className={`shrink-0 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity ${t.dueDate ? "" : "ml-auto"}`} />
              </button>
            ))}
            <div className="flex items-center gap-2 mt-1.5">
              <Plus size={13} className="text-neutral-300 shrink-0" />
              <input
                value={nySak}
                onChange={(e) => setNySak(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lagNySak(); } }}
                placeholder="Ny sak koblet til denne speakeren — Enter"
                className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                data-testid="speaker-ny-sak-input"
              />
            </div>
          </Seksjon>

          {aktivitet.length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {aktivitet.slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
